import { CacheStrategy, DataSourceStrategy } from '@rammetec/data-access';
import { AUDIT_FIELD_DESCRIPTORS, AUDIT_FOREIGN_KEYS } from './_auditFields.js';
import { SYSTEM_ALLOWED_DATA_SOURCES } from './dataSourcePolicy.js';
import { INVOICE_IMPORT } from '../types/invoice-import.js';
import { FIELD_LIMITS } from '../validation/fieldLimits.js';
/**
 * Descriptor for NF-e XML staging (org/{orgId}/invoice-import).
 *
 * No lifecycle fields (transactional); business `status` only. Auditable.
 * XML content is in Storage (`xmlStoragePath`), not in the document.
 *
 * Firestore-only por ora — foge do padrão `BUSINESS_ALLOWED_DATA_SOURCES` das demais
 * entidades de negócio de propósito.
 *
 * Motivo: esta entidade é escrita também pelo `system-api` (endpoint de ingestão de NF-e do
 * agente VectorHub). O `system-api` é o *servidor* do `/pg/v1` que o `PostgresRestDataSource`
 * chama, então ele não pode registrar essa source sem chamar a si mesmo por HTTP.
 * Ver spec 0030 (§ O nó) e request 0093 (§D2).
 *
 * Destrava quando: o `/pg/v1` for separado num serviço próprio (spec 0030), ou o `system-api`
 * registrar uma source que chame o pg **em processo** (0093 §D2-bis). Aí volta para
 * BUSINESS_ALLOWED_DATA_SOURCES.
 */
export const invoiceImportDescriptor = {
    name: INVOICE_IMPORT,
    label: 'Importação de NF-e',
    domain: 'fiscal',
    collectionPath: 'org/{orgId}/invoice-import',
    pathPatternKeys: {
        orgId: 'string',
    },
    version: 1,
    description: 'Staging record for inbound NF-e XML before promotion to Invoice',
    repositoryConfig: {
        dataSource: DataSourceStrategy.Firestore,
        // 0124: writes also come from system-api (agent ingest) — InMemory cache would hide new XML forever.
        cache: CacheStrategy.NoCache,
    },
    allowedDataSources: SYSTEM_ALLOWED_DATA_SOURCES,
    fields: [
        { name: 'id', label: 'ID', type: 'string', required: true, description: 'Unique import identifier' },
        {
            name: 'sha256',
            label: 'SHA-256',
            type: 'string',
            required: true,
            maxLength: FIELD_LIMITS.SHA256,
            description: 'File content hash (technical idempotency key)',
        },
        {
            name: 'accessKey',
            label: 'Chave de acesso',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.NFE_ACCESS_KEY,
            description: 'NFe access key (44 digits)',
        },
        {
            name: 'number',
            label: 'Número',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.CODE,
        },
        {
            name: 'series',
            label: 'Série',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.CODE,
        },
        { name: 'issueDate', label: 'Data de emissão', type: 'date', required: false },
        {
            name: 'issuerCnpj',
            label: 'CNPJ emitente',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.CNPJ,
        },
        {
            name: 'issuerName',
            label: 'Emitente',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.NAME,
        },
        { name: 'totalAmount', label: 'Valor total', type: 'number', required: false },
        { name: 'itemCount', label: 'Itens', type: 'number', required: false },
        {
            name: 'status',
            label: 'Status',
            type: 'string',
            required: true,
            description: 'received | parsed | invalid | promoted | discarded',
            defaultValue: 'parsed',
        },
        {
            name: 'parseError',
            label: 'Erro de parse',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.STATUS_MESSAGE,
        },
        {
            name: 'xmlStoragePath',
            label: 'Path do XML',
            type: 'string',
            required: true,
            maxLength: FIELD_LIMITS.STORAGE_PATH,
        },
        {
            name: 'invoiceId',
            label: 'Nota fiscal',
            type: 'string',
            required: false,
            description: 'Promoted Invoice id (optional)',
        },
        {
            name: 'partyId',
            label: 'Fornecedor',
            type: 'string',
            required: false,
            description: 'Resolved supplier party (reconciliation)',
        },
        {
            name: 'pendingItemCount',
            label: 'Pendências',
            type: 'number',
            required: false,
            description: 'Unresolved items after last reconciliation; null = never reconciled',
        },
        { name: 'reconciledAt', label: 'Conciliado em', type: 'date', required: false },
        {
            name: 'sourceKind',
            label: 'Origem',
            type: 'string',
            required: true,
            description: 'manual | agent',
        },
        {
            name: 'sourceFileName',
            label: 'Arquivo',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.FILE_NAME,
        },
        {
            name: 'sourceAgentId',
            label: 'Agente',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.CODE,
        },
        {
            name: 'sourceWatcherId',
            label: 'Watcher',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.CODE,
        },
        {
            name: 'additionalSources',
            label: 'Origens adicionais',
            type: 'array',
            required: false,
            description: 'Extra origins for the same accessKey (max 10). Diagnostic only — 0094 §F.',
        },
        { name: 'detectedAt', label: 'Detectado em', type: 'date', required: false },
        ...AUDIT_FIELD_DESCRIPTORS,
    ],
    foreignKeys: [
        ...AUDIT_FOREIGN_KEYS,
        {
            fieldName: 'invoiceId',
            targetEntity: 'Invoice',
            relationship: 'many-to-one',
            displayField: 'number',
            description: 'Promoted Invoice (optional until promotion)',
        },
        {
            fieldName: 'partyId',
            targetEntity: 'Party',
            relationship: 'many-to-one',
            displayField: 'displayName',
            description: 'Resolved supplier (optional until reconciliation)',
        },
    ],
};
//# sourceMappingURL=invoice-import.descriptor.js.map